import { ReactNode, useEffect, useRef, useState } from "react";
import { Icon, IconName } from "../shell/Icon";

// The shared pieces behind docs/DESIGN.md's "add another" and "remove" patterns.
//
// ADD. A section's heading row carries a "+" on the right. The form it opens is hidden until then,
// so a screen's primary button appears only once someone has asked to add something, and a list
// that is already complete (prevagroup.com, claimed) does not invite a second one.
//
// REMOVE. A trash can on the row, and a confirmation that says what removing it DOES, not only
// what it is. Red appears only here (docs/DESIGN.md: "red only for removal").
//
// NOT ALLOWED. Shown, disabled, with the reason as its label. Administrators find things by
// seeing them; an action that vanishes for some people is an action nobody can ask about. It is
// `aria-disabled`, not `disabled`: a disabled button can't be focused, so a keyboard or screen
// reader never reaches the reason, and a phone never shows a `title`. Pressing it shows the
// reason as text beside it instead.

// How big an icon button is follows what it acts on (docs/DESIGN.md): the page ("+" for a new
// person), a section ("+" for a claimed domain), a row (a person's pencil or lock), or one item
// inside a row (the "x" on a role). Smaller as the thing it acts on gets smaller, never under
// 24px: the smallest target WCAG 2.2 allows (2.5.8).
export type IconSize = "page" | "section" | "row" | "item";

export function IconButton({ icon, label, onClick, disabled, why, danger, pressed,
                             size = "section" }: {
  icon: IconName;
  size?: IconSize;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  // Why it is disabled. Becomes the tooltip, so the reason is where the action would be.
  why?: string | null;
  danger?: boolean;
  pressed?: boolean;
}) {
  const reason = disabled && why ? why : label;
  const [told, setTold] = useState(false);
  const button = (
    <button type="button" className={`btn icon icon-${size}${danger ? " quiet-danger" : ""}`}
            aria-label={reason} title={reason} aria-disabled={disabled || undefined}
            onClick={disabled ? () => setTold(!told) : onClick}
            onBlur={() => setTold(false)}
            onKeyDown={(e) => { if (e.key === "Escape") setTold(false); }}
            aria-expanded={disabled ? undefined : pressed}>
      <Icon name={icon} />
    </button>
  );
  if (!disabled || !why) return button;
  return (
    <span className="ib-why">
      {button}
      {told && <span className="ib-why-pop" role="status">{why}</span>}
    </span>
  );
}

/** A heading with, optionally, a "+" on the right that opens the section's add form. */
export function SectionHead({ title, level = 3, addLabel, adding, onAdd, disabledWhy }: {
  title: string;
  level?: 2 | 3;
  addLabel?: string;
  adding?: boolean;
  onAdd?: () => void;
  // Set when the caller may not add: the "+" stays, disabled, saying why.
  disabledWhy?: string | null;
}) {
  const Heading = level === 2 ? "h2" : "h3";
  return (
    <div className={`sec-head sec-head-${level}`}>
      <Heading>{title}</Heading>
      {addLabel && onAdd && (
        <IconButton icon="plus" label={addLabel} onClick={onAdd} pressed={!!adding}
                    disabled={!!disabledWhy} why={disabledWhy}
                    size={level === 2 ? "page" : "section"} />
      )}
    </div>
  );
}

/** The add form a "+" opened: its fields, then Save and Cancel.
 *
 *  SAVE IS NEVER DISABLED FOR SOMETHING MISSING (docs/DESIGN.md, "Forms"): a disabled Save tells
 *  nobody what is wrong. `missing` is what still has to be done, as an instruction ("Enter a
 *  name."), or null. Pressing Save with something missing says it beside the buttons and moves
 *  focus to the first empty field. Only saving itself disables the buttons. */
export function AddForm({ children, saveLabel, missing, busy, onSave, onCancel, error, note }: {
  children: ReactNode;
  saveLabel: string;
  missing: string | null;
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
  error?: string | null;
  note?: ReactNode;
}) {
  const form = useRef<HTMLDivElement>(null);
  const [told, setTold] = useState(false);
  function save() {
    if (!missing) { setTold(false); onSave(); return; }
    setTold(true);
    const fields = form.current?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input:not([type=checkbox]):not([type=radio]):not([type=hidden]), select, textarea");
    const empty = Array.from(fields ?? []).find((f) => !f.disabled && !f.value.trim());
    empty?.focus();
  }
  return (
    <div className="add-form">
      <div className="pp-form" ref={form}>
        {children}
        <div className="pp-actions">
          <button type="button" className="btn primary sm" disabled={busy}
                  onClick={save}>{busy ? "Saving…" : saveLabel}</button>
          <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>Cancel</button>
          {told && missing && <span className="inc-error" role="alert">{missing}</span>}
        </div>
      </div>
      {note && <p className="rv-mut">{note}</p>}
      {error && <p className="inc-error">{error}</p>}
    </div>
  );
}

/** Whether a row matches what was typed: every word appears in one of its fields, ignoring case. */
export function matches(typed: string, ...fields: (string | null | undefined)[]): boolean {
  const words = typed.toLowerCase().split(/\s+/).filter(Boolean);
  const hay = fields.filter(Boolean).join(" ").toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** The search above a long list (docs/DESIGN.md, "Finding one in a long list"): labelled with
 *  what it searches, and, while narrowed, "12 of 340" with Show all. Kept on the page, not in the
 *  URL: what someone types may be a person's name. */
export function Find({ label, value, onChange, shown, total }: {
  label: string; value: string; onChange: (v: string) => void; shown: number; total: number }) {
  return (
    <div className="find">
      <label className="find-field">{label}
        <input type="search" value={value} onChange={(e) => onChange(e.target.value)} /></label>
      {value.trim() && (
        <span className="find-count" role="status">
          {shown} of {total}{" "}
          <button type="button" className="btn-link" onClick={() => onChange("")}>Show all</button>
        </span>)}
    </div>
  );
}

/** How many rows a long list shows at first, and how many more each press shows. */
export const PAGE = 100;

/** The rows a long list shows: the first PAGE, growing by PAGE. `reset` when the search changes. */
export function useShowMore(): { limit: number; more: () => void; reset: () => void } {
  const [limit, setLimit] = useState(PAGE);
  return { limit, more: () => setLimit((l) => l + PAGE), reset: () => setLimit(PAGE) };
}

/** Under a long list's table (docs/DESIGN.md, "Finding one in a long list"): the count shown, and
 *  "Show 100 more". No numbered pages. Nothing at all once every row is shown. */
export function ShowMore({ shown, total, onMore }: { shown: number; total: number; onMore: () => void }) {
  if (shown >= total) return null;
  return (
    <div className="show-more">
      <span className="find-count">{shown} of {total}</span>
      <button type="button" className="btn sm" onClick={onMore}>
        Show {Math.min(PAGE, total - shown)} more</button>
    </div>
  );
}

/** What was just ended, and how to give it back. */
export interface Ended { said: string; undo: () => Promise<unknown> }

/** Ending a role takes no confirmation (docs/DESIGN.md, "Ending something reversible"), so the
 *  note says what ended and offers Undo, until the next action. Giving it back by hand would
 *  mean remembering exactly what it was. */
export function UndoNote({ ended, busy, onUndo }: {
  ended: Ended | null; busy: boolean; onUndo: () => void }) {
  if (!ended) return null;
  return (
    <p className="pp-note" role="status">
      {ended.said}{" "}
      <button type="button" className="btn-link" disabled={busy} onClick={onUndo}>Undo</button>
    </p>
  );
}

/** A confirmation before removing something, in the native dialog (docs/DESIGN.md, Accessibility). `danger` is
 *  for removing; an action that is not a deletion but cannot be taken back (freezing a set,
 *  starting a paid run) confirms in the primary colour. */
export function ConfirmDialog({ open, title, children, confirmLabel, busy, onConfirm, onCancel,
                                danger = true }: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal?.();
    if (!open && d.open) d.close?.();
  }, [open]);
  return (
    <dialog ref={ref} className="confirm" onCancel={(e) => { e.preventDefault(); onCancel(); }}>
      <h3>{title}</h3>
      <div className="confirm-body">{children}</div>
      <div className="pp-actions">
        <button type="button" className={`btn sm ${danger ? "danger" : "primary"}`} disabled={busy}
                onClick={onConfirm}>
          {confirmLabel}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel} autoFocus>
          Cancel
        </button>
      </div>
    </dialog>
  );
}
